/**
 * The scan screen: camera on top, basket below, running total and live discount pinned to the
 * bottom. Everything here assumes one hand, a moving basket and a shop's overhead lighting, so the
 * controls are large, nothing depends on hover, and the camera failing is a supported way to work
 * rather than an error screen.
 */
import { useEffect, useRef, useState } from "react";
import { euros, quantity } from "../format.ts";
import { navigate } from "../hooks.ts";
import { useModel, type ScanEcho } from "../model.tsx";
import { useScanner } from "../scanner.ts";
import { Icon } from "../components/icons.tsx";
import { Badge, Empty, Note, Spinner } from "../components/ui.tsx";

const ECHO_MS = 2600;

export function Scan() {
	const model = useModel();
	const { basket, lastScan } = model;
	const scanner = useScanner(model.scan, basket !== null && basket.committedTripId === null);
	const manual = useRef<HTMLInputElement | null>(null);
	const [typed, setTyped] = useState("");
	const [echo, setEcho] = useState<ScanEcho | null>(null);

	useEffect(() => {
		if (lastScan === null) return;
		setEcho(lastScan);
		const timer = window.setTimeout(() => setEcho(null), ECHO_MS);
		return () => window.clearTimeout(timer);
	}, [lastScan]);

	// With no camera, typing is the only way in, so put the cursor there without being asked.
	const blocked = scanner.state === "denied" || scanner.state === "unavailable" || scanner.state === "failed";
	useEffect(() => {
		if (blocked) manual.current?.focus();
	}, [blocked]);

	const lines = basket?.lines ?? [];
	const total = basket?.totalCents ?? 0;
	const discount = model.rec?.winner?.plan.discountCents ?? null;
	const committed = basket !== null && basket.committedTripId !== null;
	const echoHit = echo !== null && echo.line !== null && echo.line.productId !== null;

	return (
		<>
			<main className="screen scan">
				<section className={blocked ? "viewfinder viewfinder-off" : "viewfinder"}>
					<video ref={scanner.video} playsInline muted autoPlay disablePictureInPicture />
					{!blocked && (
						<div className="reticle" aria-hidden="true">
							<i className="reticle-corner tl" />
							<i className="reticle-corner tr" />
							<i className="reticle-corner bl" />
							<i className="reticle-corner br" />
							<i className="reticle-beam" />
						</div>
					)}

					{scanner.state === "starting" && (
						<div className="viewfinder-overlay">
							<Spinner label="Opening the camera" />
						</div>
					)}

					{blocked && (
						<div className="viewfinder-overlay viewfinder-blocked">
							<h2>
								{scanner.state === "denied" ? "Camera blocked" : "No camera"}
							</h2>
							<p>{scanner.error}</p>
							<button type="button" className="btn btn-ghost" onClick={scanner.retry}>
								<Icon name="refresh" /> Try the camera again
							</button>
							<p className="viewfinder-fallback">
								<Icon name="keyboard" /> Until then, type the barcode digits in below. Everything else works
								exactly the same.
							</p>
						</div>
					)}

					{scanner.state === "running" && scanner.torchSupported && (
						<button
							type="button"
							className={scanner.torchOn ? "torch torch-on" : "torch"}
							onClick={scanner.toggleTorch}
							aria-pressed={scanner.torchOn}
						>
							<Icon name="torch" />
							<span>{scanner.torchOn ? "Torch on" : "Torch"}</span>
						</button>
					)}

					{echo !== null && (
						<div className={echoHit ? "echo echo-hit" : "echo echo-miss"} role="status">
							<Icon name={echoHit ? "check" : "alert"} />
							<span className="echo-text">
								{echo.error !== null
									? echo.error
									: echo.line === null || echo.line.productId === null
										? `${echo.barcode} is not an AH product`
										: echo.line.title}
							</span>
							{echo.line !== null && echoHit && <span className="echo-price">{euros(echo.line.unitPriceCents)}</span>}
						</div>
					)}
				</section>

				<form
					className={blocked ? "manual manual-primary" : "manual"}
					onSubmit={(event) => {
						event.preventDefault();
						const code = typed.replace(/\D/g, "");
						if (code.length < 8) return;
						setTyped("");
						void model.scan(code);
					}}
				>
					<Icon name="keyboard" />
					<input
						ref={manual}
						value={typed}
						onChange={(event) => setTyped(event.target.value.replace(/\D/g, "").slice(0, 13))}
						inputMode="numeric"
						autoComplete="off"
						enterKeyHint="done"
						placeholder="Type barcode digits"
						aria-label="Barcode digits"
					/>
					<button type="submit" className="btn btn-quiet" disabled={typed.replace(/\D/g, "").length < 8}>
						Add
					</button>
				</form>

				{model.basketError !== null && <Note tone="danger">{model.basketError}</Note>}

				{lines.length === 0 ? (
					<Empty title="Nothing scanned yet">
						Point the reticle at a barcode. Items land here as you go, and the card recommendation updates with
						every scan.
					</Empty>
				) : (
					<ul className="lines">
						{lines.map((line) => {
							const unknown = line.productId === null;
							const steppable = Number.isInteger(line.qty);
							return (
								<li key={line.id} className={unknown ? "line line-unknown" : "line"}>
									<span className="line-title">
										{unknown ? line.barcode : line.title}
										{unknown && <Badge tone="warn">unknown barcode</Badge>}
										{line.nationalPromo && !unknown && <Badge tone="accent">store promo</Badge>}
									</span>
									<span className="line-total">{euros(Math.round(line.unitPriceCents * line.qty))}</span>
									<span className="line-meta">
										{unknown
											? "Counted in the basket, worth nothing to the solver"
											: `${euros(line.unitPriceCents)} each${line.taxonomyLeaf === null ? "" : ` \u00b7 ${line.taxonomyLeaf}`}`}
									</span>
									<span className="line-actions">
										{steppable ? (
											<>
												<button
													type="button"
													className="step"
													aria-label={`One fewer ${line.title}`}
													onClick={() =>
														void (line.qty <= 1 ? model.removeLine(line.id) : model.setQty(line.id, line.qty - 1))
													}
												>
													<Icon name={line.qty <= 1 ? "trash" : "minus"} />
												</button>
												<output className="qty">{quantity(line.qty)}</output>
												<button
													type="button"
													className="step"
													aria-label={`One more ${line.title}`}
													onClick={() => void model.setQty(line.id, line.qty + 1)}
												>
													<Icon name="plus" />
												</button>
											</>
										) : (
											<>
												<output className="qty qty-wide">{quantity(line.qty)}</output>
												<button
													type="button"
													className="step"
													aria-label={`Remove ${line.title}`}
													onClick={() => void model.removeLine(line.id)}
												>
													<Icon name="trash" />
												</button>
											</>
										)}
									</span>
								</li>
							);
						})}
					</ul>
				)}
			</main>

			<div className="actionbar">
				<div className="actionbar-figures">
					<span className="ab-total">
						<small>Basket</small>
						{euros(total)}
					</span>
					<span className={model.recFresh ? "ab-bonus" : "ab-bonus ab-stale"}>
						<small>{model.recLoading ? "Solving\u2026" : "Bonus"}</small>
						{discount === null ? "\u2014" : `\u2212${euros(discount)}`}
					</span>
				</div>
				<button
					type="button"
					className="btn btn-primary btn-lg"
					disabled={lines.length === 0}
					onClick={() => navigate("result")}
				>
					{committed ? "Show card" : "Pick a card"}
					<Icon name="chevron" />
				</button>
			</div>
		</>
	);
}
